import * as React from 'react';
import _ from 'lodash';
import cx from 'classnames';
import {IProduct, IVideoFile} from '../../../../types/productDef';
import {ImageMode} from '@wix/wixstores-client-core/dist/es/src/media/constants';
import {Swipeable} from 'react-swipeable';
import {getMainImageRatio, isVideo} from '@wix/wixstores-client-core/dist/es/src/media/mediaService';
import s from './ResponsiveGallery.scss';
import {DotNavigation, DotNavigationTheme, ThumbnailImage, ImageLoadingBehaviorOptions} from '@wix/wix-ui-tpa';
import {
  DEFAULT_IMAGE_SIZE,
  ImageRatio,
  GalleryNavigationType,
  GalleryNavigationPosition,
  ProductPageSlotIds,
} from '../../../../constants';
import {IMediaItem} from '../../../../types/app-types';
import {ModalGalleryLayout} from '../../../ModalGallery/ModalGalleryLayout';
import {ProductImage} from '../../../ProductGallery/MainMedia/ProductImage/ProductImage';
import {ProvidedGlobalProps, withGlobalProps} from '../../../../providers/globalPropsProvider';
import {DefaultProductImage} from '@wix/wixstores-client-common-components/dist/es/src/DefaultProductImage/DefaultProductImage';
import {THUMBNAIL_SIZE} from '../../../ProductGallery/ProductGalleryLayout/ProductMediaSwiper/utils/constants';
import {SlotsPlaceholder} from '@wix/widget-plugins-ooi';
import {ShowOnMobileOnly} from '@wix/wixstores-client-common-components/dist/es/src/HOC/responsive/ShowOnMobileOnly/ShowOnMobileOnly';

const WIXVIDEO_BASEURL = 'https://wixvideo.cdn.inventica.ru/';

export enum ResponsiveGalleryDataHook {
  Root = 'responsive-gallery',
  Media = 'responsive-gallery-media',
  MediaContainer = 'responsive-gallery-media-container',
  NavigationButton = 'responsive-gallery-navigation',
  SEO_IMAGE = 'ResponsiveGalleryDataHook.SEO_IMAGE',
  NoMedia = 'responsive-gallery-new-no-media',
}

export interface ResponsiveGalleryProps extends ProvidedGlobalProps {
  imageMode: ImageMode;
  imageRatio?: ImageRatio;
  maxRatio?: number;
  media: IProduct['media'];
  productName: string;
  navigationType?: GalleryNavigationType;
  navigationPosition?: GalleryNavigationPosition;
}

interface ResponsiveGalleryState {
  currentIndex: number;
  currentEffect: 'fadeout' | 'fadein';
  media: IProduct['media'];
  renderWidth: number;
}

class ResponsiveGalleryComponent extends React.Component<ResponsiveGalleryProps, ResponsiveGalleryState> {
  private readonly ref: React.RefObject<HTMLDivElement>;
  private readonly defaultRenderWidth = 100;

  public static defaultProps = {
    imageRatio: ImageRatio.AUTO,
    openZoom: _.noop,
    classNames: {},
  };

  public constructor(props: ResponsiveGalleryProps) {
    super(props);
    this.ref = React.createRef();
    this.state = {
      currentEffect: null,
      currentIndex: 0,
      media: [],
      renderWidth: this.defaultRenderWidth,
    };
  }

  private isMediaVideo(index: number) {
    const media = this.props.media[index];
    return !!(media.mediaType === 'VIDEO' && media.videoFiles && media.videoFiles[0]);
  }

  private get ratio() {
    const {media, imageRatio, maxRatio} = this.props;
    const mainRatio = getMainImageRatio(media[0], imageRatio);
    const {height, width} = imageRatio === ImageRatio.AUTO ? media[0] : mainRatio.ratio;
    const ratio = width === 0 || height === 0 ? 3 / 2 : height / width;

    if (maxRatio && ratio > maxRatio) {
      return maxRatio;
    }
    return ratio;
  }

  private readonly navigateToMedia = (currentIndex: number) => {
    /* istanbul ignore if: todo(sagi) */
    if (this.props.media.length <= 1) {
      return;
    }

    this.fadeoutMedia();
    setTimeout(() => {
      this.setState(
        {currentIndex},
        this.isMediaVideo(currentIndex) ? () => setTimeout(() => this.fadeinMedia(), 100) : undefined
      );
    }, 100);
  };

  /* istanbul ignore next: todo(sagi) */
  private readonly goRight = () => {
    let nextIndex = this.state.currentIndex - 1;
    if (nextIndex === -1) {
      nextIndex = this.props.media.length - 1;
    }
    this.navigateToMedia(nextIndex);
  };

  /* istanbul ignore next: todo(sagi) */
  private readonly goLeft = () => {
    let nextIndex = this.state.currentIndex + 1;
    if (nextIndex > this.props.media.length - 1) {
      nextIndex = 0;
    }
    this.navigateToMedia(nextIndex);
  };

  private readonly renderImage = (
    mediaItem: IMediaItem,
    onImageLoaded?: () => any,
    onExpandIconEnterKeyPress?: () => any
  ) => {
    const {
      imageMode,
      productName,
      globals: {isQuickView},
    } = this.props;

    return (
      <ProductImage
        mediaItem={mediaItem}
        imageMode={imageMode}
        productName={productName}
        imageLoaded={onImageLoaded}
        fluid={true}
        stretchImage={true}
        isSelected={true}
        isZoomModalEnabled={!isQuickView}
        onExpandIconEnterKeyPress={onExpandIconEnterKeyPress}
      />
    );
  };

  private readonly fadeinMedia = () => {
    /* istanbul ignore next: can't test this */
    this.setState({currentEffect: 'fadein'});
  };

  private readonly fadeoutMedia = () => {
    /* istanbul ignore next: can't test this */
    this.setState({currentEffect: 'fadeout'});
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private readonly renderMedia = () => {
    const {currentEffect, currentIndex} = this.state;
    const {
      media,
      globals: {experiments},
    } = this.props;
    const shouldShowSlotOnBottom = experiments?.mediaSlotOnBottomForResponsiveGallery;
    const currentMedia = media[currentIndex];
    const video = currentMedia.mediaType === 'VIDEO' && currentMedia.videoFiles && currentMedia.videoFiles[0];
    const paddingTop = Math.ceil(this.ratio * 100);

    return (
      <div data-hook={ResponsiveGalleryDataHook.MediaContainer}>
        {shouldShowSlotOnBottom && (
          /* istanbul ignore next : TODO: Yuvalz add test */
          <ShowOnMobileOnly>
            <div className={s.mediaSlot}>
              <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageMedia1} />
            </div>
          </ShowOnMobileOnly>
        )}
        <ModalGalleryLayout externalSelectedIndex={currentIndex}>
          {(modalZoom) => {
            return (
              <Swipeable onSwipedLeft={this.goLeft} onSwipedRight={this.goRight} trackMouse={false}>
                <div
                  key={currentIndex}
                  onClick={modalZoom.open}
                  data-hook={ResponsiveGalleryDataHook.Media}
                  style={{paddingTop: `${paddingTop}%`}}
                  className={cx(s.media, s[currentEffect], {
                    'modal-zoom-icon': modalZoom.isEnabled,
                  })}>
                  {video ? (
                    this.renderVideo(video)
                  ) : (
                    <div className={s.imageContainer}>
                      {this.renderImage(currentMedia, this.fadeinMedia, modalZoom.open)}
                    </div>
                  )}
                </div>
              </Swipeable>
            );
          }}
        </ModalGalleryLayout>
      </div>
    );
  };

  private renderVideo(video: IVideoFile) {
    const src = `${WIXVIDEO_BASEURL}${video.url}#t=0.001`;
    return <video controls controlsList="nodownload" src={src} />;
  }

  private readonly renderNavigation = () => {
    const {productName, media, navigationType, navigationPosition, globals} = this.props;
    const {currentIndex} = this.state;

    if (media.length <= 1) {
      return;
    }

    const ariaLabelsArray = media.map((m) => m.altText || productName);

    if (
      navigationType === GalleryNavigationType.THUMBNAILS &&
      navigationPosition === GalleryNavigationPosition.BOTTOM
    ) {
      return (
        <div className={s.navigation}>
          <div className={s.thumbnailNavigation} data-testid="thumbnail-navigation">
            {media.map((mediaItem, index) => {
              const isSelected = index === currentIndex;
              return (
                <button
                  key={index}
                  type="button"
                  className={s.thumbnailButton}
                  data-testid={`thumbnail-button-${index}`}
                  onClick={() => this.navigateToMedia(index)}
                  aria-label={ariaLabelsArray[index]}
                  aria-pressed={isSelected}>
                  <ThumbnailImage
                    sourceHeight={THUMBNAIL_SIZE}
                    sourceWidth={THUMBNAIL_SIZE}
                    width={THUMBNAIL_SIZE}
                    height={THUMBNAIL_SIZE}
                    src={mediaItem.url}
                    alt={ariaLabelsArray[index]}
                    isSEOBot={globals.isSEO}
                    shouldUseLQIP
                    encoding="AVIF"
                    loadingBehavior={ImageLoadingBehaviorOptions.none}
                  />
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className={s.navigation}>
        <DotNavigation
          currentIndex={currentIndex}
          data-hook={ResponsiveGalleryDataHook.NavigationButton}
          length={media.length}
          onSelect={this.navigateToMedia}
          showBorder
          theme={DotNavigationTheme.Dark}
          aria-label={ariaLabelsArray}
        />
      </div>
    );
  };

  public static getDerivedStateFromProps(
    props: ResponsiveGalleryProps,
    state: ResponsiveGalleryState
  ): {currentIndex: 0; media: IProduct['media']} {
    if (!_.isEqual(props.media, state.media)) {
      return {currentIndex: 0, media: props.media};
    }
    return null;
  }

  public componentDidMount(): void {
    this.setState({renderWidth: this.ref.current.clientWidth});
  }

  public render(): JSX.Element {
    const {media} = this.props;
    return (
      <div data-hook={ResponsiveGalleryDataHook.Root} ref={this.ref}>
        {media.length > 0 ? this.renderMedia() : this.renderNoMedia()}
        {this.renderNavigation()}
        {this.renderSEO()}
      </div>
    );
  }

  private renderNoMedia() {
    return (
      <DefaultProductImage
        imageSize={DEFAULT_IMAGE_SIZE}
        className={s.noMedia}
        dataHook={ResponsiveGalleryDataHook.NoMedia}
        isDynamicSize={this.props.globals.experiments.EnableDynamicSizeDefaultImage}
      />
    );
  }

  private getVideo(mediaItem: IMediaItem) {
    return mediaItem.videoFiles?.[0];
  }

  private renderSEO() {
    const {media} = this.props;

    return (
      <ul className={cx(s.seo_only)}>
        {media.map((m, index) => (
          <li key={`seo-media-${index}-${m.id || m.url}`} data-hook={ResponsiveGalleryDataHook.SEO_IMAGE}>
            <div style={{aspectRatio: (1 / this.ratio).toString()}}>
              {isVideo(m) ? this.renderVideo(this.getVideo(m)) : this.renderImage(m)}
            </div>
          </li>
        ))}
      </ul>
    );
  }
}

export const ResponsiveGallery = withGlobalProps(ResponsiveGalleryComponent);
